import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';

import { useDebounce } from 'hooks/useDebounce';
import { useNewFilters } from 'hooks/useFilters';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useInfiniteTransactions } from 'services/repositories/transactions/transactions';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { SEARCH_DEBOUNCE_MS, withoutShortSearchTerms } from 'support/helpers/filters/filters';
import { routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { RelationshipV2, TransactionDTO } from 'support/types';
import { Filters } from 'components/Filters/Filters';
import { InfiniteScroll } from 'components/ListTable/InfiniteScroll/InfiniteScroll';
import { ListTable } from 'components/ListTable/ListTable';
import { TableHeader, TableItem } from 'components/ListTable/types';
import { TransactionStatusDisplay } from 'components/TransactionStatusDisplay/TransactionStatusDisplay';

import { getRelationshipTransactionsFilterConfig } from './tableConfig';

type TransactionsTabProps = {
  relationship: RelationshipV2;
};

export const TransactionsTab = ({ relationship }: TransactionsTabProps) => {
  const { t } = useTranslation();
  const { data: currentPartner } = useCurrentPartner();
  const formattedGoLiveDate = relationship.go_live_date ? formatDayAndTime(relationship.go_live_date) : '';

  const filterConfig = useMemo(() => getRelationshipTransactionsFilterConfig(), []);

  const { filters, filterValues, updateFilters, clearAllFilters, perPage } = useNewFilters(
    filterConfig,
    `relationship-transactions-${relationship.id}`,
  );

  const hasFilters = filterValues && filterValues.length > 0;
  const debouncedFilterValues = useDebounce(filterValues, SEARCH_DEBOUNCE_MS);
  const differentFilters = !isEqual(debouncedFilterValues, filterValues);

  const filterValuesWithRelationship = useMemo(
    () => [
      { key: 'relationship_id', value: relationship.id },
      ...withoutShortSearchTerms(debouncedFilterValues ?? [], filters),
    ],
    [relationship.id, debouncedFilterValues, filters],
  );

  const {
    data: messagesData,
    isLoading: messagesDataLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteTransactions({
    variables: {
      query: {
        filterValues: filterValuesWithRelationship,
        perPage,
      },
    },
  });

  const tableHeaders: Array<TableHeader> = [
    { label: t('connections:relationshipDetails.transactions.tableHeaders.documentNumber') },
    { label: t('connections:relationshipDetails.transactions.tableHeaders.status') },
    { label: t('connections:relationshipDetails.transactions.tableHeaders.date') },
  ];

  const buildRow = (transaction: TransactionDTO): Array<TableItem> => {
    return [
      {
        element: transaction.externalMessageRef?.length ? transaction.externalMessageRef : EMPTY_FIELD,
        key: `external-ref-${transaction.id}`,
        className: 'text-gray-900 font-medium group-hover:text-procuros-green-600',
        longText: true,
      },
      {
        element: <TransactionStatusDisplay transaction={transaction} currentPartner={currentPartner} />,
        key: 'transaction-status',
      },
      {
        element: <span className="tabular-nums">{formatDayAndTime(transaction.createdAt)}</span>,
        key: 'created-at',
      },
    ];
  };

  const data = messagesData?.pages?.flatMap((page) => page.items);
  const emptyData = data?.length === 0;

  const showEndOfListBanner = !messagesDataLoading && !hasNextPage && Boolean(relationship.go_live_date);

  return (
    <div className="space-y-4">
      <Filters
        propertyFilters={filters}
        onFiltersChange={updateFilters}
        filterValues={filterValues}
        clearAllFilters={clearAllFilters}
      />

      <ListTable<TransactionDTO>
        headers={tableHeaders}
        data={data}
        rowBuilder={buildRow}
        rowClickPath={(transaction) => routeToTransactionPage(transaction.internalType, transaction.id)}
        isLoading={messagesDataLoading || (differentFilters && emptyData)}
        pagination={
          <InfiniteScroll
            hasMore={Boolean(hasNextPage)}
            loadMore={fetchNextPage}
            loading={isFetchingNextPage}
            noMoreItemsEnabled={showEndOfListBanner}
            noMoreItemsLabel={t('connections:relationshipDetails.transactions.endOfList', {
              date: formattedGoLiveDate,
            })}
            className={'pt-2'}
          />
        }
        emptyState={
          <ListTable.EmptyState
            background="gray"
            cols={tableHeaders.length}
            cta={
              hasFilters
                ? {
                    children: t('common:table.withFilters.emptyState.cta'),
                    onClick: clearAllFilters,
                    analyticsId: 'relationship_transactions:clear_filters',
                  }
                : undefined
            }
          >
            {hasFilters
              ? t('common:table.withFilters.emptyState.title')
              : t('connections:relationshipDetails.transactions.emptyState')}
          </ListTable.EmptyState>
        }
      />
    </div>
  );
};
